<template>
  <b-modal
    ref="modal"
    :class="[$style.modal, aside && $style.asideModal]"
  >
    <div :class="[$style.modalContent, aside && $style.asideContent]">
      <header :class="$style.modalHeader">
        <div
          data-testid="close-icon"
          :class="$style.modalClose"
          @click="close"
        >
          <slot name="closeIcon" >
            <b-svg
              name="close"
              :size="40"
            />
          </slot>
        </div>

        <div class="flex">
          <bvc-button
            v-if="allowDelete"
            data-testid="delete-button"
            :class="$style.coverButton"
            @click="() => onDelete(activeIndex)"
            size="md"
          >
            <b-svg
              name="trash"
              size="24px"
              color="red"
            />
          </bvc-button>

          <bvc-button
            v-if="allowPick"
            :class="$style.coverButton"
            @click="pickImage"
            size="lg"
          >
            <slot name="pickBtn"/>
          </bvc-button>

          <bvc-button
            data-testid="share-button"
            v-if="allowShare"
            :class="$style.coverButton"
            @click="shareImage"
            size="lg"
          >
            <slot name="shareBtn"/>
          </bvc-button>

          <slot name="otherBtn" />
        </div>

      </header>

      <b-slider
        ref="slider"
        :aside="aside"
        :slides="images"
        :options="options"
        :key-name="keyName"
        :total="total"
        @slide-change="onSliderSlideChange"
        :manual-init="true"
        :show-nav-buttons-background="showNavButtonsBackground"
        :show-pagination="showPagination"
      >
        <template #slide="{ item }">
          <div
            v-if="showSlideHeaderPagination"
            class="b-h3 b-text-center b-pb-6 b-text-white"
            :style="{'max-width': maxImageSize}"
          >
            {{ activeIndex + 1}} {{t('components.common.b-image-slider.of')}} {{total}}
          </div>
          <img
            data-testid="swiper-slider-image"
            :key="item.image_id ? item.image_id : item.id"
            :src="item.url ? item.url : item.image"
            loading="lazy"
            :style="{'max-width': maxImageSize, 'max-height': maxImageSize}"
          />
          <div
            v-if="showImageDescription"
            class="b-text-center b-pt-6 b-text-white"
            data-testid="swiper-slider-image-description"
            :style="{'max-width': maxImageSize}"
          >
            {{ item.description }}
          </div>
        </template>
      </b-slider>
    </div>
    <slot name="aside" :activeIndex="activeIndex"/>
  </b-modal>
</template>

<script>
import BvcButton from '@/booksy-vue-components/components/b-button/index.vue'
import { computed } from 'vue'
import { useModal } from '@/composables/modal'
import { useCore } from '@/composables/core'

export default {

  emits: [
    'active-index',
    'delete-image',
    'pick-image',
    'share-image',
  ],
  name: 'b-image-slider',

  data () {
    return {
      activeIndex: 0,
    }
  },

  props: {
    images: {
      type: Array,
      required: true,
    },
    options: {
      type: Object,
      default: () => {
        return {
          pagination: {
            el: '.swiper-pagination',
            type: 'fraction',
            renderFraction: function (currentClass, totalClass) {
              return '<span class="' + currentClass + '"></span>'
                + ' of '
                + '<span class="' + totalClass + '"></span>'
            },
          },
        }
      },
    },
    keyName: {
      type: String,
      default: 'id',
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
    allowPick: {
      type: Boolean,
      default: false,
    },
    allowShare: {
      type: Boolean,
      default: false,
    },
    aside: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
    },
    showImageDescription: {
      type: Boolean,
      default: false,
    },
    showNavButtonsBackground: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    showSlideHeaderPagination: {
      type: Boolean,
      default: false,
    },
    showDeleteConfirm: {
      type: Boolean,
      default: true,
    },
  },

  components: {
    BvcButton,
  },

  setup (props, { emit }) {
    const { t } = useCore()
    const modal = useModal()

    const maxImageSize = computed(() => {
      if (props.showImageDescription) {
        return '80%'
      }
      return '100%'
    })

    const onDelete = (activeIndex) => {
      if (props.showDeleteConfirm) {
        modal.open({
          width: 400,
          content: {
            $icon: {
              url: 'ico_delete.svg',
              source: 'img',
            },
            $title: t('components.common.b-image-slider.delete-image'),
            $text: t('components.common.b-image-slider.delete-image-description'),
          },
          buttons: [
            {
              $text: t('delete'),
              size: 'lg',
              theme: 'alert',
              '@click': () => {
                emit('delete-image', activeIndex)
              },
            },
            {
              $text: t('cancel'),
              size: 'lg',
              '@click': modal.close,
            },
          ],
        })
      } else {
        emit('delete-image', activeIndex)
      }
    }

    return {
      maxImageSize,
      onDelete,
      t,
    }
  },

  computed: {
    asideModalStyles () {
      if (this.aside) {
        return 'display: flex; flex-direction: row'
      }

      return null
    },
  },

  methods: {
    onSliderSlideChange (activeIndex) {
      this.activeIndex = activeIndex
      this.$emit('active-index', activeIndex)
    },
    pickImage () {
      this.$emit('pick-image', this.activeIndex)
    },
    shareImage () {
      this.$emit('share-image', this.activeIndex)
    },
    open (index = 0) {
      this.$refs.modal.open()
      if (!this.$refs.slider.swiper) {
        this.$nextTick(() => {
          this.$refs.slider.init(index)
        })
      } else {
        this.$refs.slider.slideTo(index)
      }

      this.$emit('active-index', index)
    },

    close () {
      this.$refs.modal.close()
    },
  },

}
</script>

<style lang="scss" module>
@layer legacy-views {

.modal {
  padding: 0 !important;
  background-color: var(--color-black) !important;

  &Header {
    height: 48px;
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    justify-content: space-between;
  }
  &Close {
    color: var(--color-white);
    cursor: pointer;
  }
  &DeleteImage {
    color: $color-event-red;
  }
  &Content {
    height: 100%;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;

    &.asideContent {
      width: calc(100% - 320px);
    }
  }
}

.coverButton {
  margin-left: 16px;
  cursor: pointer;
}

.asideModal {
  display: flex;
  flex-direction: row;
}

}</style>
